import React, { useEffect, useRef, useState } from 'react';
import { Switch } from '@roger/ui-components';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { ClinicSelect } from '../clinic/ClinicSelect';
import {
  getAgencyClinics,
  updateAgencyClinic,
} from '../../services/clinicsApi';
import type { AgencyClinicUpdate } from '../../services/clinicsApi';
import { buildAgencyClinicSelectOptions } from '../../utils/agencyClinicDisplayName';
import type { AgencyClinicResponse } from '../../services/api';

/**
 * Per-clinic policy for what Roger EMR creates when an intake is pushed
 * (EMR-1384).
 *
 * One clinic at a time, picked from the selector, with one row per task — the
 * Scheduling Points shape. An N-clinic x 3-toggle grid stopped being readable
 * well before a 20-clinic agency, and it repeated the CC60 hint under every
 * row.
 *
 * The three toggles ship at today's behavior — CMS-485 and the 30-Day Summary
 * on, the Case Conference & 60-Day Summary off — so an agency that never opens
 * this card sees no change.
 */

type PatientCreationSettings = {
  auto_create_cms485: boolean;
  auto_create_thirty_day_summary: boolean;
  auto_create_cc60: boolean;
};

const SETTING_FIELDS: {
  key: keyof PatientCreationSettings;
  label: string;
  description: string;
  hint?: string;
}[] = [
  {
    key: 'auto_create_cms485',
    label: 'CMS-485',
    description:
      'Create the plan-of-care order with the episode. A CMS-485 you tag on an intake document is created either way.',
  },
  {
    key: 'auto_create_thirty_day_summary',
    label: '30-Day Summary',
    description: 'Schedule the communication 30 days after the episode starts.',
  },
  {
    key: 'auto_create_cc60',
    label: 'Case Conference & 60-Day Summary',
    description: 'Schedule the visit on the last day of the episode.',
    hint: 'Also requires the Case Conference feature for your agency.',
  },
];

/**
 * The clinic the dropdown shows at the top.
 *
 * ClinicSelect sorts its options by display label — `getAgencyClinicDisplayName`,
 * not `clinic.name` — with a plain `localeCompare`, and passes
 * `sortOptions={false}` so that order is final. Defaulting to `data[0]`, the
 * API's first row, therefore pre-selects a clinic that is usually *not* the one
 * at the top of the list; on a card whose whole job is per-clinic writes, that
 * is a "which clinic am I changing?" hazard.
 *
 * `buildAgencyClinicSelectOptions` is the same label builder ClinicSelect uses,
 * and defaults `totalClinicCount` to `clinics.length` exactly as ClinicSelect
 * computes it. This card passes no `agencyName`, matching the `undefined` the
 * component sees. Keep this mirroring ClinicSelect: a default that is *almost*
 * the top entry is no better than no fix.
 */
const alphabeticallyFirstClinicId = (
  clinics: AgencyClinicResponse[]
): string => {
  const sorted = buildAgencyClinicSelectOptions(clinics).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  return sorted[0]?.value ?? '';
};

const PatientCreationSection: React.FC = () => {
  const { canEdit } = useAdminSectionAccess('patient-creation');
  const { agencyId, showSuccess, showError } = useStore(
    useShallow(state => ({
      agencyId: state.user?.agency?.id,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [settings, setSettings] = useState<
    Record<string, PatientCreationSettings>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  // A read that failed tells us nothing about this agency's settings, so the
  // card must not offer to write one. Without this the screen would report the
  // product defaults as if they were the stored answers, and an admin could
  // save a value derived from a failed read. This is also why the card keeps
  // its own fetch instead of taking Admin.tsx's clinic list as a prop: that
  // list turns a failed load into an empty array, which would render here as
  // "no clinics to configure" — a different claim than "this did not load".
  const [loadFailed, setLoadFailed] = useState(false);
  // Keyed `clinicId:field`, not by clinic and not by field alone. Two tasks on
  // one clinic can be toggled in quick succession, and the selector can move
  // while a save is in flight; either one clearing the other's entry would
  // re-enable a switch mid-save.
  const [savingKeys, setSavingKeys] = useState<Set<string>>(() => new Set());
  // Latest known server value / on-screen value, per clinic, held in refs
  // rather than state: nothing renders from them, and handleToggle must read
  // the freshest values. Reading the render's closure instead would let a
  // second toggle build its payload from a stale snapshot and silently revert
  // a change the server had already accepted. Keying by clinic id is also what
  // makes a late response safe — see handleToggle.
  const savedSettingsRef = useRef<Record<string, PatientCreationSettings>>({});
  const settingsRef = useRef<Record<string, PatientCreationSettings>>({});

  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const data = await getAgencyClinics(agencyId);
        if (cancelled) return;

        const loaded: Record<string, PatientCreationSettings> = {};
        data.forEach(clinic => {
          // `?? default`, never Boolean(...): during a CloudFront-first deploy
          // an old API task answers without these fields, and Boolean(undefined)
          // would render the two default-ON toggles as OFF — reporting CMS-485
          // and the 30-Day Summary as disabled for a clinic that is in fact
          // creating both. The fallbacks are the product defaults, which are
          // exactly what an old API does, so an absent field reads true.
          loaded[clinic.id] = {
            auto_create_cms485: clinic.auto_create_cms485 ?? true,
            auto_create_thirty_day_summary:
              clinic.auto_create_thirty_day_summary ?? true,
            auto_create_cc60: clinic.auto_create_cc60 ?? false,
          };
        });

        setClinics(data);
        setSettings(loaded);
        settingsRef.current = loaded;
        savedSettingsRef.current = loaded;
        // Never sit in an empty state while clinics exist, and start on the
        // clinic the dropdown shows first — see alphabeticallyFirstClinicId.
        setSelectedClinicId(
          current => current || alphabeticallyFirstClinicId(data)
        );
      } catch (err) {
        console.error('Failed to load patient creation settings:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  const handleToggle = async (
    clinicId: string,
    field: keyof PatientCreationSettings,
    nextValue: boolean
  ) => {
    // Read through the refs, not the render's closure: a save on another task
    // may have landed since this handler was created.
    const saved = savedSettingsRef.current[clinicId];
    const current = settingsRef.current[clinicId];
    if (!saved || !current) return;

    const previousValue = saved[field];
    const savingKey = `${clinicId}:${field}`;
    settingsRef.current = {
      ...settingsRef.current,
      [clinicId]: { ...current, [field]: nextValue },
    };
    setSettings(existing => ({
      ...existing,
      [clinicId]: { ...existing[clinicId], [field]: nextValue },
    }));
    setSavingKeys(existing => new Set(existing).add(savingKey));

    try {
      // Only the field that moved, never all three. Two tasks can be toggled
      // before the first save answers, and a request carrying all three would
      // also carry this handler's snapshot of the other two: whichever request
      // commits last would write the sibling task back to its pre-toggle value
      // while both switches still read as saved. `AgencyClinicUpdate` is
      // all-optional and `AgencyClinicCRUD.update` dumps it with
      // `exclude_unset`, so a one-field body updates exactly one column and the
      // two writes commute regardless of the order they land in.
      const payload: AgencyClinicUpdate = { [field]: nextValue };
      await updateAgencyClinic(clinicId, payload);
      // Every write here is addressed by `clinicId`, never by the current
      // selection, so a response landing after the selector moved updates the
      // clinic it belongs to and cannot repaint the newly selected clinic's
      // values under its id. That is why the selector stays usable during a
      // save rather than being blocked.
      //
      // Patch the one field instead of recording this handler's whole
      // snapshot: a sibling save still in flight has an optimistic value in
      // `settingsRef` the server has not accepted, and banking it as saved
      // would let a later rollback restore a value that was never stored.
      savedSettingsRef.current = {
        ...savedSettingsRef.current,
        [clinicId]: {
          ...savedSettingsRef.current[clinicId],
          [field]: nextValue,
        },
      };
      showSuccess('Patient creation settings updated');
    } catch (err) {
      console.error('Failed to update patient creation settings:', err);
      // Roll this field back onto the latest on-screen values, not onto the
      // snapshot taken when this save started — that snapshot predates any
      // sibling toggle that has succeeded since, and restoring it wholesale
      // would erase an accepted change from the screen.
      const latest = settingsRef.current[clinicId] ?? current;
      const rolledBack = { ...latest, [field]: previousValue };
      settingsRef.current = { ...settingsRef.current, [clinicId]: rolledBack };
      setSettings(existing => ({ ...existing, [clinicId]: rolledBack }));
      showError('Failed to save patient creation settings');
    } finally {
      setSavingKeys(existing => {
        const remaining = new Set(existing);
        remaining.delete(savingKey);
        return remaining;
      });
    }
  };

  const selectedSettings = settings[selectedClinicId];

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2
            className='h-4 w-4 animate-spin'
            aria-label='Loading settings'
          />
          Loading settings...
        </div>
      );
    }
    if (loadFailed) {
      return (
        <p className='text-sm text-destructive'>
          These settings did not load. Reload the page to see them.
        </p>
      );
    }
    if (clinics.length === 0) {
      return (
        <p className='text-sm text-muted-foreground'>
          No clinics to configure.
        </p>
      );
    }
    if (!selectedSettings) {
      return (
        <p className='text-sm text-muted-foreground'>
          Select a clinic to see its settings.
        </p>
      );
    }

    return (
      <div className='space-y-3'>
        {SETTING_FIELDS.map(({ key, label, description, hint }) => {
          const isSaving = savingKeys.has(`${selectedClinicId}:${key}`);
          const inputId = `patient-creation-${key}`;
          return (
            <div
              key={key}
              className='flex items-center justify-between gap-4 rounded-md border p-3'
            >
              <div className='min-w-0 space-y-0.5'>
                <label htmlFor={inputId} className='text-sm font-medium'>
                  {label}
                </label>
                <p className='text-xs text-muted-foreground'>{description}</p>
                {hint && (
                  <p className='text-xs text-muted-foreground'>{hint}</p>
                )}
              </div>
              <div className='flex shrink-0 items-center gap-2'>
                {isSaving && (
                  <Loader2
                    className='h-4 w-4 animate-spin text-muted-foreground'
                    aria-label='Saving'
                  />
                )}
                <Switch
                  id={inputId}
                  checked={selectedSettings[key]}
                  disabled={isSaving || !canEdit}
                  onCheckedChange={value =>
                    void handleToggle(selectedClinicId, key, value)
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className='space-y-6'>
      <div className='space-y-1.5'>
        <p className='text-sm text-muted-foreground'>
          What Roger creates automatically when an intake is pushed for this
          clinic
        </p>
        {!loadFailed && clinics.length > 0 && (
          <div className='mt-3 w-full md:max-w-sm'>
            <ClinicSelect
              clinics={clinics}
              value={selectedClinicId}
              onChange={e => setSelectedClinicId(e.target.value)}
              placeholder='Select a clinic'
            />
          </div>
        )}
      </div>
      <div>{renderBody()}</div>
    </div>
  );
};

export default PatientCreationSection;
